import {
  AppBar,
  Box,
  ButtonBase,
  Container,
  Drawer,
  MenuItem,
  MenuList,
  Slide,
  Toolbar,
  useScrollTrigger,
  useTheme,
  Typography,
  List,
  ListItem,
  Collapse,
  ListItemText,
  MenuListProps,
  styled,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import React, { useEffect, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, navigate } from "gatsby";
import Logo from "../../../assets/svgs/horde.svg";
import LetterLogo from "../../../assets/svgs/horde-h.svg";
import useIsMobile from "../../../hooks/useIsMobile";
import MenuButton from "./MenuButton";
import QRButton from "../../QRButton";
import COLORS from "../../../constants/colors";
import ArrowDownChevron from "../../icons/ArrowDownChevron";
import globalTheme from "../../../styles/theme";

/**
 * Wrapper component for navigation elements.
 * This component handles the visibility of navigation elements based on screen size.
 */

const isScrolledMoreThan10px = () => {
  if (typeof window !== "undefined") {
    return window.scrollY > 10;
  }
  return false;
};

interface ScrollProps {
  children: React.ReactElement;
}

function HideOnScroll(props: ScrollProps) {
  const { children } = props;

  const trigger = useScrollTrigger({
    threshold: 200,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

interface Props {
  bgColor?: string;
}

// MenuList component that overwrites bgcolor to dark blue and color to white
const StyledMenuList = styled(MenuList)(() => ({
  backgroundColor: COLORS.DARK_BLUE[100],
  color: "white",
  "& .MuiMenuItem-root": {
    "&.Mui-selected": {
      backgroundColor: COLORS.DARK_BLUE[100],
      color: "white",
      "&:hover": {
        backgroundColor: COLORS.DARK_BLUE[100],
      },
    },
    "&:hover": {
      backgroundColor: COLORS.DARK_BLUE[100],
    },
  },
}));

const MenuListComponent: React.FC<MenuListProps> = (props) => (
  <StyledMenuList {...props} />
);

const StyledList = styled(List)(() => ({
  backgroundColor: COLORS.DARK_BLUE[100],
  color: "white",
  "& .MuiMenuItem-root": {
    "&.Mui-selected": {
      backgroundColor: COLORS.DARK_BLUE[100],
      color: "white",
      "&:hover": {
        backgroundColor: COLORS.DARK_BLUE[100],
      },
    },
    "&:hover": {
      backgroundColor: COLORS.DARK_BLUE[100],
    },
  },
}));

const ListComponent: React.FC<ListProps> = (props) => <StyledList {...props} />;

const Header = ({ bgColor, ...rest }: Props) => {
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isServiceMenuVisible, setIsServiceMenuVisible] = useState(false);

  const handleMouseEnter = () => {
    setIsMenuVisible(true);
  };

  const handleMouseLeave = () => {
    setIsMenuVisible(false);
  };

  const handleServiceMouseEnter = () => {
    setIsServiceMenuVisible(true);
  };

  const handleServiceMouseLeave = () => {
    setIsServiceMenuVisible(false);
  };

  const theme = useTheme();
  const isMobile = useIsMobile();

  const handleScroll = () => {
    setScrolled(isScrolledMoreThan10px());
  };

  useEffect(() => {
    setCurrentPath(window.location.pathname);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Closes the menu drawer when the screen size changes from mobile to desktop.
  useEffect(() => {
    if (!isMobile) {
      setMenuDrawerOpen(false);
    }
  }, [isMobile]);

  /**
   * Toggles the state of the menu drawer.
   * @param {boolean} open - Indicates whether the menu drawer should be open or closed.
   */
  const toggleDrawer = (open: boolean) => () => {
    setMenuDrawerOpen(open);
  };

  const handleSubMenuToggle = (title: string) => {
    setOpenSubMenu(openSubMenu === title ? null : title);
  };

  const serviceMenu = [
    { title: "Kredittscore", url: "/tjenester/kredittscore" },
    { title: "Boligverdi", url: "/tjenester/boligverdi" },
  ];
  const lanMenu = [
    { title: "Lån", url: "/lan" },
    { title: "Refinansiering", url: "/lan/refinansiering" },
    { title: "Forbrukslån", url: "/lan/forbrukslan" },
    {
      title: "Lån med sikkerhet i bolig",
      url: "/lan/lan-med-sikkerhet-i-bolig",
    },
  ];

  // Menu items
  const menuItems = [
    { title: "Hjem", url: "/" },
    {
      title: "Lån og refinansiering",
      url: "/lan",
      subMenu: lanMenu,
    },
    { title: "Lån", url: "/lan", subMenu: lanMenu },
    {
      title: "Tjenester",
      url: "/kredittscore",
      subMenu: serviceMenu,
    },
    { title: "Horde-appen", url: "/app" },
    { title: "Kampanjer", url: "/gjeldfri" },
    { title: "Om oss", url: "/om-oss" },
  ];

  // Navigates to the home page when the logo is clicked
  const handleLogoClick = () => {
    navigate("/");
  };

  return (
    <>
      <HideOnScroll {...rest}>
        <AppBar elevation={0}>
          <Container maxWidth="xl">
            <Toolbar
              disableGutters
              sx={{
                transition: "padding 0.3s ease",
                paddingTop: scrolled ? "32px" : "32px",
                paddingBottom: scrolled ? "32px" : "32px",
                [theme.breakpoints.down("md")]: {
                  paddingTop: "22px",
                  paddingBottom: "22px",
                },
              }}
            >
              <Box
                role="presentation"
                display={{ xs: "none", lg: "flex" }}
                flexGrow={1}
                alignItems="center"
                sx={{
                  marginLeft: "60px",
                }}
              >
                <ButtonBase
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleLogoClick();
                    }
                  }}
                  sx={{
                    border: "1pX solid transparent",
                    "&:focus": {
                      border: `1px solid ${COLORS.WHITE[100]}`,
                      borderRadius: "8px",
                    },
                  }}
                  onClick={handleLogoClick}
                  disableRipple
                  disableTouchRipple
                  aria-label="Gå til forsiden"
                >
                  <Logo width="190px" />
                </ButtonBase>
              </Box>
              <Box
                role="presentation"
                display={{ xs: "flex", lg: "none" }}
                flexGrow={1}
                alignItems="center"
              >
                <ButtonBase
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleLogoClick();
                    }
                  }}
                  sx={{
                    border: "1pX solid transparent",
                    "&:focus": {
                      border: `1px solid ${COLORS.WHITE[100]}`,
                      borderRadius: "8px",
                    },
                  }}
                  onClick={handleLogoClick}
                  disableRipple
                  disableTouchRipple
                  aria-label="Gå til forsiden"
                >
                  <LetterLogo width="60px" />
                </ButtonBase>
              </Box>
              {/* Desktop menu. Is only visible if screen size is desktop */}
              <Box
                role="presentation"
                display="flex"
                alignItems="center"
                gap="24px"
                minWidth={0}
              >
                <Box
                  role="presentation"
                  display={{ xs: "none", md: "flex" }}
                  minWidth={0}
                >
                  <Box
                    display="flex"
                    component="nav"
                    aria-label="Hovednavigasjon"
                    gap="8px"
                    minWidth={0}
                  >
                    {menuItems.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          "@media (min-width:1045px)": {
                            display: index === 2 ? "none" : "flex",
                          },
                          "@media (max-width:1045px)": {
                            display: index === 1 ? "none" : "flex",
                          },
                        }}
                      >
                        {item.subMenu ? (
                          <Box
                            sx={{
                              position: "relative",
                            }}
                          >
                            <ButtonBase
                              disableRipple
                              onFocus={
                                item.title === "Tjenester"
                                  ? handleServiceMouseEnter
                                  : handleMouseEnter
                              }
                              onMouseEnter={
                                item.title === "Tjenester"
                                  ? handleServiceMouseEnter
                                  : handleMouseEnter
                              }
                              onMouseLeave={
                                item.title === "Tjenester"
                                  ? handleServiceMouseLeave
                                  : handleMouseLeave
                              }
                              sx={{
                                cursor: "pointer",
                                paddingTop: "2px",
                                "&:focus": {
                                  outline: COLORS.WHITE[100],
                                },
                              }}
                            >
                              <Typography
                                fontSize={16}
                                alignItems="center"
                                display="flex"
                                aria-current={item.subMenu.some(
                                  (sub) => sub.url === currentPath
                                )}
                              >
                                {item.title}
                                <ArrowDownChevron
                                  stroke={COLORS.WHITE[100]}
                                  sx={{
                                    width: "15px",
                                    height: "15px",
                                    marginLeft: "5px",
                                  }}
                                />
                              </Typography>
                            </ButtonBase>

                            {(item.title === "Tjenester"
                              ? isServiceMenuVisible
                              : isMenuVisible) && (
                              <Box
                                onFocus={
                                  item.title === "Tjenester"
                                    ? handleServiceMouseEnter
                                    : handleMouseEnter
                                }
                                onMouseEnter={
                                  item.title === "Tjenester"
                                    ? handleServiceMouseEnter
                                    : handleMouseEnter
                                }
                                onMouseLeave={
                                  item.title === "Tjenester"
                                    ? handleServiceMouseLeave
                                    : handleMouseLeave
                                }
                                style={{
                                  position: "absolute",
                                  zIndex: 1000,
                                  paddingTop: "10px",
                                }}
                              >
                                <Box
                                  style={{
                                    backgroundColor: "#272443",
                                    borderRadius: "18px",
                                    paddingTop: "12px",
                                    paddingBottom: "12px",
                                  }}
                                >
                                  {item.subMenu.map((subItem, subIndex) => (
                                    <Link
                                      onBlur={
                                        // if last item, handle mouse leave
                                        subIndex === item.subMenu.length - 1
                                          ? handleMouseLeave
                                          : undefined
                                      }
                                      key={subIndex}
                                      to={subItem.url}
                                      aria-current={currentPath === subItem.url}
                                      className="hover-link"
                                      style={{
                                        textDecoration: "none",
                                        display: "flex",
                                        padding: "10px",
                                        alignItems: "flex-start",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Typography
                                        color={
                                          currentPath === subItem.url
                                            ? globalTheme.palette.secondary.main
                                            : COLORS.WHITE[100]
                                        }
                                        variant="loanCardLabelBold"
                                        fontWeight="regular"
                                        paddingLeft="16px"
                                        paddingRight="80px"
                                        sx={{
                                          textAlign: "left",
                                          ".hover-link:hover &": {
                                            textDecoration: "underline",
                                            textUnderlineOffset: "5px",
                                          },
                                        }}
                                      >
                                        {subItem.title}
                                      </Typography>
                                    </Link>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Link
                            to={item.url}
                            aria-current={currentPath === item.url}
                          >
                            {item.title}
                          </Link>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box
                  role="presentation"
                  display="flex"
                  alignItems="center"
                  flexGrow={1}
                >
                  <QRButton
                    color="menu"
                    label="Last ned appen"
                    variant={isMobile ? "outlined" : "contained"}
                  />
                </Box>
              </Box>
              {/* Button for opening mobile menu. Only visible if screen size is mobile */}
              <Box
                role="presentation"
                display={{ xs: "flex", md: "none" }}
                ml="10px"
              >
                <MenuButton
                  ariaLabel="Åpne meny"
                  label="Meny"
                  icon={<MenuIcon />}
                  onClick={toggleDrawer(true)}
                />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      <Toolbar
        sx={{
          transition: "height 0.3s ease",
          height: scrolled ? 64 : 100,
          [theme.breakpoints.down("md")]: {
            height: scrolled ? 55 : 105,
          },
        }}
      />

      {/* Mobile menu drawer. Is only open if screen size is mobile */}
      <Drawer
        open={menuDrawerOpen && isMobile}
        onClose={toggleDrawer(false)}
        anchor="right"
      >
        <Box
          role="presentation"
          display="flex"
          justifyContent="flex-end"
          mb="12px"
        >
          <MenuButton
            ariaLabel="Lukk meny"
            label="Lukk"
            icon={<CloseIcon />}
            onClick={toggleDrawer(false)}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap="40px"
          role="presentation"
        >
          <nav aria-label="Hovednavigasjon">
            <MenuListComponent>
              {menuItems
                .filter((i, idx) => idx !== 2)
                .map((item, index) => (
                  <React.Fragment key={index}>
                    <MenuItem
                      tabIndex={item.subMenu ? 0 : -1}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleSubMenuToggle(item.title);
                        }
                      }}
                      onClick={() => handleSubMenuToggle(item.title)}
                    >
                      {item.subMenu ? (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "20px",
                            paddingY: "26px",
                          }}
                        >
                          {item.title}
                        </Typography>
                      ) : (
                        <Link
                          to={item.url}
                          aria-current={currentPath === item.url}
                        >
                          {item.title}
                        </Link>
                      )}
                      {item.subMenu ? (
                        openSubMenu === item.title ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )
                      ) : null}
                    </MenuItem>
                    {item.subMenu && (
                      <Collapse in={openSubMenu === item.title}>
                        <ListComponent>
                          {item.subMenu.map((subItem, subIndex) => (
                            <ListItem
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  navigate(subItem.url);
                                }
                              }}
                              component="div"
                              key={subIndex}
                              onClick={() => navigate(subItem.url)}
                              sx={{ pl: 4 }}
                            >
                              <ListItemText
                                primary={subItem.title}
                                primaryTypographyProps={{
                                  color:
                                    currentPath === subItem.url
                                      ? globalTheme.palette.secondary.main
                                      : COLORS.WHITE[100],
                                }}
                              />
                            </ListItem>
                          ))}
                        </ListComponent>
                      </Collapse>
                    )}
                  </React.Fragment>
                ))}
            </MenuListComponent>
          </nav>
          <Box role="presentation" display="flex">
            <QRButton color="menu" label="Last ned appen" />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
